import { useRef, useState } from 'react';
import type { GuestColumnGroup } from './GuestTable';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  GUEST_PRIMARY_BUTTON_SX,
  GUEST_SECONDARY_BUTTON_SX,
  GUEST_TEXT_BUTTON_SX,
  TRACKER_SURFACE_SX,
} from './guestTableStyles';
import { parseCsv, summarizeImportRows } from '../../../lib/partifulCsv';
import { NAME_LIST_SCOPES, type NameListScope } from '../../../lib/guestNameList';
import {
  applyMapping,
  detectMapping,
  guestFieldsFor,
  validateMapping,
  type ColumnMapping,
  type GuestField,
} from '../../../lib/columnMapping';
import { newTagsSentence, previewNewTags, splitTagCell, tagSlug } from '../../../lib/guestTags';
import ImportColumnMapper from './ImportColumnMapper';
import {
  TRACKER_GOING_LABELS,
  type GuestTag,
  type TrackerImportRow,
} from '../../../services/tracker-api-client';

/** Rows shown to the host as a sample, and used for shape-based detection. */
const SHAPE_SAMPLE = 5;

interface PendingMapping {
  fileName: string;
  headers: string[];
  rows: string[][];
  mapping: ColumnMapping;
}

interface PendingImport {
  fileName: string;
  rows: TrackerImportRow[];
  skipped: { line: number; reason: string }[];
  /** "Creates 4 new tags: rugby lot, work, …", or '' when it creates none. */
  newTags: string;
}

export interface GuestConsoleProps {
  onImport: (rows: TrackerImportRow[]) => Promise<void>;
  onExport: () => Promise<void>;
  /** Put the guests' names on the clipboard, one per line. */
  onCopyNames: (scope: NameListScope) => void;
  importing: boolean;
  exporting: boolean;
  /*
    The console CLEARS the notice — picking a new file makes the last outcome
    stale — but no longer renders it. The outcome of an import, a copy or a BULK
    TAG APPLY used to render inside this panel, which is collapsed by default on
    every trip that has guests: "Nothing needed changing." arrived at height
    zero behind a folded accordion, and the Apply button read as having done
    nothing at all. It renders beside the roster now, in GuestsTab.
  */
  onDismissNotice: () => void;
  visibleGroups: readonly GuestColumnGroup[];
  onToggleGroup: (group: GuestColumnGroup) => void;
  /**
   * The trip's tag vocabulary, or null when this API has no tags at all.
   *
   * Null is not "no tags yet" — that is `[]`. Null hides the Tags and Dietary
   * dropdowns entirely, because mapping a column the API will ignore is worse
   * than not offering it: the host confirms a mapping and the data vanishes.
   */
  tags: GuestTag[] | null;
}

const COLUMN_GROUP_LABELS: ReadonlyArray<{ key: GuestColumnGroup; label: string }> = [
  { key: 'roster', label: 'Contact details' },
  { key: 'logistics', label: 'Travel details' },
];

const EXPORT_MENU_ID = 'tracker-import-menu';

/**
 * The console above the guest table: bring a roster in, take one out.
 *
 * Import parses the CSV in the browser and POSTs JSON — the API takes rows, not
 * a file, so there is no upload plumbing anywhere in the stack. The host sees
 * the parsed split before anything is written.
 *
 * Export is nested under Import rather than standing beside it: taking the
 * roster out is the rarer of the two, and a second top-level button next to
 * "Import Partiful CSV" invites the misread that it exports Partiful's file.
 * The chevron is its own button, so the menu opens from the keyboard too.
 */
export function GuestConsole({
  onImport,
  onExport,
  onCopyNames,
  importing,
  exporting,
  onDismissNotice,
  visibleGroups,
  onToggleGroup,
  tags,
}: GuestConsoleProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState<PendingImport | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [mapper, setMapper] = useState<PendingMapping | null>(null);
  // Detection and the dropdown read the same list, so a field the mapper will
  // not show can never be silently pre-filled behind it.
  const fields = guestFieldsFor(tags !== null);

  /*
    Any guest list, not one vendor's. The file is split into a header row and
    data rows, the columns are guessed from the headers, and the host confirms
    that guess against their own data before a single row is read. Nothing about
    the shape is assumed — a file whose columns are called "Column 1" is mapped
    by hand and imports exactly as well as a Partiful export does.
  */
  const handleFile = async (file: File) => {
    setParseError(null);
    onDismissNotice();
    setMapper(null);
    try {
      const table = parseCsv(await file.text()).filter((row) =>
        row.some((cell) => cell.trim().length > 0),
      );
      if (table.length < 2) {
        setParseError(
          'That file has no rows under its header. Check it exported with the guests in it.',
        );
        return;
      }
      const [headers, ...dataRows] = table;
      setMapper({
        fileName: file.name,
        headers,
        rows: dataRows,
        mapping: detectMapping(headers, dataRows.slice(0, SHAPE_SAMPLE), fields),
      });
    } catch {
      setParseError("That file couldn't be read as a CSV.");
    }
  };

  /** Turns the confirmed mapping into import rows and hands them to the plan. */
  const handleMappingConfirmed = () => {
    if (!mapper) return;
    // The button is disabled while the mapping has errors, but the guard lives
    // here too: a mapping with no name column would import a file of blank
    // guests, and that must not depend on a disabled prop staying correct.
    const { errors } = validateMapping(mapper.mapping, mapper.headers, mapper.rows.slice(0, SHAPE_SAMPLE));
    if (errors.length > 0) return;
    const applied = applyMapping(mapper.mapping, mapper.rows);
    if (applied.rows.length === 0) {
      setParseError('No rows had a name in the column you picked, so there is nobody to import.');
      return;
    }
    setPending({
      fileName: mapper.fileName,
      rows: applied.rows.map((row) => ({
        inviteeId: row.tripsbyId,
        name: row.name,
        email: row.email,
        phone: row.phone,
        status: row.status,
        rsvpAt: row.rsvpAt,
        invitedBy: row.invitedBy,
        plusOneOf: row.plusOneOf,
        /*
          Notes was mapped, shown to the host as mapped, and then never put on
          the row — the import confirmed a column it did not read. It goes out
          now, alongside the two tag columns.

          Slugged here rather than on the server so the "creates 4 new tags"
          count below is computed from the same values that get written. Two
          folds would let the host be promised four and given five.
        */
        notes: row.notes,
        ...(tags
          ? {
              tagSlugs: splitTagCell(row.tags).map(tagSlug),
              dietarySlugs: splitTagCell(row.dietary).map(tagSlug),
            }
          : {}),
      })),
      skipped: applied.skipped,
      newTags: tags ? newTagsSentence(previewNewTags(applied.rows, tags)) : '',
    });
    setMapper(null);
  };

  const preview = pending ? summarizeImportRows(pending.rows) : null;

  return (
    <Paper elevation={0} sx={{ ...TRACKER_SURFACE_SX, p: { xs: 2, sm: 2.5 } }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
      >
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            color: 'var(--tsb-fg-muted)',
            fontSize: 12.5,
            lineHeight: 1.55,
          }}
        >
          Import reads any guest list — a Partiful or Knot export, a contacts export, your own
          spreadsheet. You confirm what each column is before anything is read. Export CSV lives
          under Import.
        </Typography>

        {/*
          Hiding a column group is a view preference, not a filter on the data —
          worth being explicit about, because a host who has collapsed the
          contact columns and then exports will still get every field in the CSV.
        */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          role="group"
          aria-label="Columns shown in the guest table"
          sx={{ flexShrink: 0 }}
        >
          {COLUMN_GROUP_LABELS.map(({ key, label }) => {
            const shown = visibleGroups.includes(key);
            return (
              <Chip
                key={key}
                size="small"
                label={label}
                variant={shown ? 'filled' : 'outlined'}
                onClick={() => onToggleGroup(key)}
                aria-pressed={shown}
                sx={{
                  cursor: 'pointer',
                  ...(shown
                    ? { bgcolor: 'var(--tsb-brand-tint)', color: 'var(--tsb-brand-ink)' }
                    : { color: 'var(--tsb-fg-muted)' }),
                }}
              />
            );
          })}
        </Stack>

        <Box
          component="input"
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          aria-label="Guest list file"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            // Reset so re-picking the same file fires change again.
            event.target.value = '';
            if (file) void handleFile(file);
          }}
          sx={{ display: 'none' }}
        />

        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
          {exporting ? (
            <Typography
              role="status"
              sx={{ color: 'var(--tsb-fg-muted)', fontSize: 12.5, whiteSpace: 'nowrap' }}
            >
              Preparing the export…
            </Typography>
          ) : null}
          <Box sx={{ display: 'inline-flex' }}>
            <Button
              variant="outlined"
              startIcon={<UploadFileRoundedIcon />}
              disabled={importing}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                ...GUEST_SECONDARY_BUTTON_SX,
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                whiteSpace: 'nowrap',
              }}
            >
              Import guest list
            </Button>
            <Button
              variant="outlined"
              aria-label="More guest list actions"
              aria-haspopup="menu"
              aria-expanded={menuAnchor ? true : undefined}
              aria-controls={menuAnchor ? EXPORT_MENU_ID : undefined}
              onClick={(event) => setMenuAnchor(event.currentTarget)}
              sx={{
                ...GUEST_SECONDARY_BUTTON_SX,
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                // One shared hairline between the two halves.
                ml: '-1px',
                minWidth: 40,
                px: 0,
              }}
            >
              <ExpandMoreRoundedIcon sx={{ fontSize: 20 }} />
            </Button>
          </Box>
        </Stack>
      </Stack>

      <Menu
        id={EXPORT_MENU_ID}
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 0.5,
            minWidth: 240,
            borderRadius: 'var(--tsb-radius-md)',
            bgcolor: 'var(--tsb-bg-surface)',
            boxShadow: 'var(--tsb-shadow-2)',
            backgroundImage: 'none',
          },
        }}
      >
        <MenuItem
          disabled={exporting}
          onClick={() => {
            setMenuAnchor(null);
            void onExport();
          }}
          sx={{ borderRadius: 'var(--tsb-radius-sm)', mx: 0.5, py: 1 }}
        >
          <ListItemIcon sx={{ color: 'var(--tsb-fg-muted)', minWidth: 32 }}>
            <DownloadRoundedIcon sx={{ fontSize: 18 }} />
          </ListItemIcon>
          <ListItemText
            primary="Export CSV"
            secondary="Every column, with the ids that let you import it back."
            primaryTypographyProps={{
              sx: { color: 'var(--tsb-fg)', fontSize: 13.5, fontWeight: 600 },
            }}
            secondaryTypographyProps={{ sx: { color: 'var(--tsb-fg-muted)', fontSize: 12 } }}
          />
        </MenuItem>
        {/*
          The CSV answers a different question. It carries every column and the
          ids that import it back, which is exactly what makes it the wrong
          thing to hand a restaurant — nobody pastes a spreadsheet into a message
          to the maitre d'. These two are names and nothing else.
        */}
        {NAME_LIST_SCOPES.map((scope) => (
          <MenuItem
            key={scope.value}
            onClick={() => {
              setMenuAnchor(null);
              onCopyNames(scope.value);
            }}
            sx={{ borderRadius: 'var(--tsb-radius-sm)', mx: 0.5, py: 1 }}
          >
            <ListItemIcon sx={{ color: 'var(--tsb-fg-muted)', minWidth: 32 }}>
              <ContentCopyRoundedIcon sx={{ fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText
              primary={scope.label}
              secondary={scope.hint}
              primaryTypographyProps={{
                sx: { color: 'var(--tsb-fg)', fontSize: 13.5, fontWeight: 600 },
              }}
              secondaryTypographyProps={{ sx: { color: 'var(--tsb-fg-muted)', fontSize: 12 } }}
            />
          </MenuItem>
        ))}
      </Menu>

      {parseError ? (
        <Alert severity="warning" sx={{ mt: 2 }} onClose={() => setParseError(null)}>
          {parseError}
        </Alert>
      ) : null}

      {mapper ? (() => {
        const sample = mapper.rows.slice(0, SHAPE_SAMPLE);
        const { errors, warnings } = validateMapping(mapper.mapping, mapper.headers, sample);
        const named = applyMapping(mapper.mapping, mapper.rows);
        return (
          <ImportColumnMapper
            fileName={mapper.fileName}
            headers={mapper.headers}
            sampleRows={sample}
            mapping={mapper.mapping}
            errors={errors}
            warnings={warnings}
            rowCount={named.rows.length}
            skippedCount={named.skipped.length}
            busy={importing}
            fields={fields}
            onChange={(field: GuestField, columnIndex?: number) =>
              setMapper((previous) => {
                if (!previous) return previous;
                const next = { ...previous.mapping };
                if (columnIndex === undefined) delete next[field];
                else next[field] = columnIndex;
                return { ...previous, mapping: next };
              })
            }
            onCancel={() => setMapper(null)}
            onConfirm={handleMappingConfirmed}
          />
        );
      })() : null}

      {pending && preview ? (
        <Box
          sx={{
            mt: 2,
            p: 2,
            border: '1px solid var(--tsb-border-soft)',
            borderRadius: 'var(--tsb-radius-md)',
            bgcolor: 'var(--tsb-bg-elevated)',
          }}
        >
          <Typography sx={{ color: 'var(--tsb-fg)', fontSize: 14, fontWeight: 600 }}>
            {`${preview.total} ${preview.total === 1 ? 'guest' : 'guests'} in ${pending.fileName}`}
          </Typography>
          <Typography sx={{ mt: 0.5, color: 'var(--tsb-fg-body)', fontSize: 13, lineHeight: 1.6 }}>
            {`${preview.counts.going} ${TRACKER_GOING_LABELS.going} · ${preview.counts.maybe} ${TRACKER_GOING_LABELS.maybe} · ${preview.counts.cant_go} ${TRACKER_GOING_LABELS.cant_go} · ${preview.counts.invited} ${TRACKER_GOING_LABELS.invited}`}
            {/* Blank status cells keep whatever RSVP the guest already has, so they
                are counted separately — folding them into Invited would promise the
                host a change the import will not make. */}
            {preview.counts.unchanged > 0
              ? ` · ${preview.counts.unchanged} keeping their current RSVP`
              : ''}
            {preview.plusOnes > 0
              ? ` · ${preview.plusOnes} plus-${preview.plusOnes === 1 ? 'one' : 'ones'}`
              : ''}
          </Typography>
          <Typography
            sx={{ mt: 0.75, color: 'var(--tsb-fg-muted)', fontSize: 12.5, lineHeight: 1.6 }}
          >
            Importing updates RSVPs for guests already on the list and adds anyone new. Arrival
            dates, hotels, notes and everything else you have entered are left alone.
          </Typography>
          {/*
            Forty tags on a 137-guest roster is what a mistyped spreadsheet
            column produces, and after the import there is no cheap way back. It
            is stated before the button, not after it.
          */}
          {pending.newTags ? (
            <Typography sx={{ mt: 0.75, color: 'var(--tsb-fg-body)', fontSize: 12.5 }}>
              {pending.newTags}
            </Typography>
          ) : null}
          {pending.skipped.length ? (
            <Typography sx={{ mt: 0.75, color: 'var(--tsb-warning-ink)', fontSize: 12.5 }}>
              {`${pending.skipped.length} ${pending.skipped.length === 1 ? 'row' : 'rows'} skipped — no name.`}
            </Typography>
          ) : null}
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button
              variant="contained"
              disabled={importing}
              onClick={async () => {
                await onImport(pending.rows);
                setPending(null);
              }}
              sx={GUEST_PRIMARY_BUTTON_SX}
            >
              {importing ? 'Importing…' : `Import ${preview.total}`}
            </Button>
            <Button
              variant="text"
              disabled={importing}
              onClick={() => setPending(null)}
              sx={{ ...GUEST_TEXT_BUTTON_SX, color: 'var(--tsb-fg-muted)' }}
            >
              Cancel
            </Button>
          </Stack>
        </Box>
      ) : null}
    </Paper>
  );
}

export default GuestConsole;
